import { useNavigate, useSearch } from "@tanstack/react-router";

import { useCreateNutrient, useNutrient } from "@/lib/api/nutrients";
import { newULID } from "@/lib/api/client";
import { ErrorState } from "@/components/error-state";

import {
  NutrientForm,
  emptyToUndef,
  parseOptNum,
  type NutrientFormValues,
} from "./form";

export function NutrientNewPage() {
  const navigate = useNavigate();
  const { from } = useSearch({ from: "/chemistry-profile/nutrients/new" });
  const create = useCreateNutrient();
  // When cloning (`?from=<id>`) we pre-fill the form from the source nutrient
  // (e.g. a read-only system one) so the user gets an editable tenant copy.
  const source = useNutrient(from);

  function submit(values: NutrientFormValues) {
    create.mutate(
      {
        id: newULID(),
        name: values.name,
        manufacturer: emptyToUndef(values.manufacturer),
        base_dose_ml_per_gal: parseOptNum(values.base_dose_ml_per_gal),
        delta_ec_per_ml_per_gal: parseOptNum(values.delta_ec_per_ml_per_gal),
        delta_ph_per_ml_per_gal: parseOptNum(values.delta_ph_per_ml_per_gal),
        mixing_order_rank: Number(values.mixing_order_rank),
        contains_calcium: !!values.contains_calcium,
        contains_phosphate: !!values.contains_phosphate,
        is_biological: !!values.is_biological,
        is_ph_adjuster: !!values.is_ph_adjuster,
        notes: emptyToUndef(values.notes),
      },
      {
        onSuccess: () => navigate({ to: "/chemistry-profile/nutrients" }),
      },
    );
  }

  if (from && source.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (from && source.error)
    return <ErrorState error={source.error} retry={source.refetch} />;

  const initial =
    from && source.data
      ? { ...source.data, name: `${source.data.name} (copy)` }
      : undefined;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">
        {initial ? "Clone nutrient" : "New nutrient"}
      </h1>
      <NutrientForm
        initial={initial}
        submitting={create.isPending}
        onSubmit={submit}
        submitLabel={initial ? "Create copy" : "Create"}
        banner={
          create.error ? (
            <ErrorState error={create.error} retry={() => create.reset()} />
          ) : undefined
        }
      />
    </div>
  );
}
